(() => {
  const CUSTOM_SORT_VALUE = 'discount-descending';
  const CUSTOM_SORT_PARAM = 'xoa_sort';
  const NATIVE_SORT_PARAM = 'sort_by';
  const PAGE_FETCH_CONCURRENCY = 4;
  const pageCache = new Map();
  let activeCollectionRequest = 0;

  const getActiveSort = () => new URLSearchParams(window.location.search).get(CUSTOM_SORT_PARAM);

  const syncSortControls = () => {
    const customSortIsActive = getActiveSort() === CUSTOM_SORT_VALUE;

    document.querySelectorAll(`select option[data-custom-sort="${CUSTOM_SORT_VALUE}"]`).forEach((option) => {
      const select = option.closest('select');
      if (!select) return;

      if (customSortIsActive) {
        select.value = CUSTOM_SORT_VALUE;
        select.name = CUSTOM_SORT_PARAM;
      } else if (select.name === CUSTOM_SORT_PARAM) {
        select.name = NATIVE_SORT_PARAM;
      }
    });
  };

  const compareProductsByDiscount = (productA, productB) => {
    const discountDifference = Number(productB.dataset.discount) - Number(productA.dataset.discount);
    if (discountDifference !== 0) return discountDifference;

    return Number(productA.dataset.sortOrder) - Number(productB.dataset.sortOrder);
  };

  const updateProductPositions = (grid, products) => {
    products.forEach((product, index) => {
      product.style.setProperty('--animation-order', index + 1);
      grid.appendChild(product);
    });
  };

  const preserveProductStyles = (products) => {
    const getStyleHref = (link) => new URL(link.getAttribute('href'), window.location.href).href;
    const loadedStyles = new Set(
      Array.from(document.head.querySelectorAll('link[rel="stylesheet"][href]'), getStyleHref)
    );

    products.forEach((product) => {
      product.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
        const href = getStyleHref(link);
        if (loadedStyles.has(href)) {
          link.remove();
          return;
        }

        loadedStyles.add(href);
        document.head.appendChild(link);
      });
    });
  };

  const sortVisibleProductGrid = (grid) => {
    const customSortIsActive = getActiveSort() === CUSTOM_SORT_VALUE;
    const appliedSort = customSortIsActive ? CUSTOM_SORT_VALUE : 'native';
    if (grid.dataset.appliedClientSort === appliedSort) return;

    const products = Array.from(grid.children).filter((item) => item.matches('.grid__item[data-discount]'));
    if (products.length === 0) return;

    if (customSortIsActive) {
      products.sort(compareProductsByDiscount);
    } else if (grid.dataset.appliedClientSort === CUSTOM_SORT_VALUE) {
      products.sort((productA, productB) => Number(productA.dataset.sortOrder) - Number(productB.dataset.sortOrder));
    } else {
      grid.dataset.appliedClientSort = appliedSort;
      return;
    }

    grid.dataset.appliedClientSort = appliedSort;
    updateProductPositions(grid, products);
  };

  const getCollectionRequest = (grid) => {
    const params = new URLSearchParams(window.location.search);
    params.delete(CUSTOM_SORT_PARAM);
    params.delete(NATIVE_SORT_PARAM);
    params.delete('page');
    params.delete('section_id');
    params.sort();

    const query = params.toString();
    const key = `${window.location.pathname}?${query}#${grid.dataset.id}`;

    return { key, params };
  };

  const fetchProductPage = async (grid, pageNumber, request) => {
    const params = new URLSearchParams(request.params);
    params.set('section_id', grid.dataset.id);
    params.set('page', pageNumber);

    const url = `${window.location.pathname}?${params.toString()}`;
    let html = pageCache.get(url);

    if (!html) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Unable to load collection page ${pageNumber}`);
      html = await response.text();
      pageCache.set(url, html);
    }

    const pageGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
    if (!pageGrid) throw new Error(`Collection page ${pageNumber} did not contain a product grid`);

    return Array.from(pageGrid.children).filter((item) => item.matches('.grid__item[data-discount]'));
  };

  const fetchPagesWithConcurrency = async (pageNumbers, task) => {
    const results = new Array(pageNumbers.length);
    let nextIndex = 0;

    const worker = async () => {
      while (nextIndex < pageNumbers.length) {
        const resultIndex = nextIndex;
        nextIndex += 1;
        results[resultIndex] = await task(pageNumbers[resultIndex]);
      }
    };

    const workerCount = Math.min(PAGE_FETCH_CONCURRENCY, pageNumbers.length);
    await Promise.all(Array.from({ length: workerCount }, worker));
    return results;
  };

  const sortEntireCollection = async (grid) => {
    const pageCount = Number(grid.dataset.pageCount || 1);
    if (pageCount <= 1 || getActiveSort() !== CUSTOM_SORT_VALUE) return;

    const request = getCollectionRequest(grid);
    if (grid.dataset.loadedDiscountSort === request.key || grid.dataset.loadingDiscountSort === request.key) return;

    grid.dataset.loadingDiscountSort = request.key;
    const requestId = ++activeCollectionRequest;
    const collection = grid.closest('.collection');
    collection?.classList.add('loading');
    grid.setAttribute('aria-busy', 'true');

    try {
      const currentPage = Number(grid.dataset.currentPage || 1);
      const pageNumbers = Array.from({ length: pageCount }, (_, index) => index + 1);
      const productPages = await fetchPagesWithConcurrency(pageNumbers, (pageNumber) => {
        if (pageNumber === currentPage) {
          return Array.from(grid.children).filter((item) => item.matches('.grid__item[data-discount]'));
        }

        return fetchProductPage(grid, pageNumber, request);
      });

      const currentGrid = document.getElementById('product-grid');
      if (
        requestId !== activeCollectionRequest ||
        currentGrid !== grid ||
        getActiveSort() !== CUSTOM_SORT_VALUE ||
        getCollectionRequest(grid).key !== request.key
      ) {
        return;
      }

      const pageSize = Number(grid.dataset.pageSize || productPages[0]?.length || 1);
      const pageStart = (currentPage - 1) * pageSize;
      const sortedProducts = productPages.flat().sort(compareProductsByDiscount);
      const visibleProducts = sortedProducts.slice(pageStart, pageStart + pageSize);

      preserveProductStyles(sortedProducts);
      grid.replaceChildren(...visibleProducts);
      grid.dataset.appliedClientSort = CUSTOM_SORT_VALUE;
      grid.dataset.loadedDiscountSort = request.key;
      updateProductPositions(grid, visibleProducts);

      grid.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    } catch (error) {
      console.error('Discount sorting could not load every collection page.', error);
    } finally {
      if (document.getElementById('product-grid') === grid) {
        delete grid.dataset.loadingDiscountSort;
        grid.removeAttribute('aria-busy');
        if (getActiveSort() === CUSTOM_SORT_VALUE && getCollectionRequest(grid).key === request.key) {
          collection?.classList.remove('loading');
        }
      }
    }
  };

  const syncPaginationLinks = () => {
    const customSortIsActive = getActiveSort() === CUSTOM_SORT_VALUE;

    document.querySelectorAll('#ProductGridContainer .pagination-wrapper a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);

      if (customSortIsActive) {
        url.searchParams.delete(NATIVE_SORT_PARAM);
        url.searchParams.set(CUSTOM_SORT_PARAM, CUSTOM_SORT_VALUE);
      } else {
        url.searchParams.delete(CUSTOM_SORT_PARAM);
      }

      link.href = url.toString();
    });
  };

  const applyDiscountSort = () => {
    syncSortControls();
    const grid = document.getElementById('product-grid');
    if (grid) {
      sortVisibleProductGrid(grid);
      sortEntireCollection(grid);
    }
    syncPaginationLinks();
  };

  const prepareSortInput = (event) => {
    const select = event.target.closest('select');
    if (!select?.querySelector(`option[data-custom-sort="${CUSTOM_SORT_VALUE}"]`)) return;

    if (select.value === CUSTOM_SORT_VALUE) {
      select.name = CUSTOM_SORT_PARAM;
    } else {
      select.name = NATIVE_SORT_PARAM;
    }
  };

  const preserveCustomSortInLink = (event) => {
    if (getActiveSort() !== CUSTOM_SORT_VALUE) return;

    const link = event.target.closest('facet-remove a[href]');
    if (!link) return;

    const url = new URL(link.href, window.location.origin);
    url.searchParams.delete(NATIVE_SORT_PARAM);
    url.searchParams.set(CUSTOM_SORT_PARAM, CUSTOM_SORT_VALUE);
    link.href = url.toString();
  };

  let updateQueued = false;
  const queueUpdate = () => {
    if (updateQueued) return;
    updateQueued = true;

    window.requestAnimationFrame(() => {
      updateQueued = false;
      applyDiscountSort();
    });
  };

  document.addEventListener('input', prepareSortInput, true);
  document.addEventListener('click', preserveCustomSortInLink, true);
  document.addEventListener('keyup', preserveCustomSortInLink, true);

  const observer = new MutationObserver(queueUpdate);
  observer.observe(document.body, { childList: true, subtree: true });

  applyDiscountSort();
})();
